import useOnClickOutside from "hooks/useOnClickOutside";
import {
  useCallback,
  useMemo,
  ComponentPropsWithoutRef,
  useState,
  ComponentType,
  useRef,
  ReactNode,
  useEffect,
} from "react";
import { FormControl } from "react-bootstrap";

type Base = {
  onUpdate: (arg: string) => Promise<void>;
  show: boolean;
  children: string;
  className?: string;
} & { [key: string]: any };

/**
 * get file extension from name
 */
function getFileExt(name = "") {
  const fileExt = name.split(".");
  if (fileExt.length < 2) {
    // fallback to default
    return "";
  }
  return fileExt[fileExt.length - 1] ?? "";
}

/**
 * render element that can be changed to an input
 */
const withEditable =
  <Props extends Base>(
    Element:
      | ComponentType<
          Omit<Props, "onUpdate" | "show" | "children"> & {
            children?: ReactNode;
          }
        >
      | string,
  ) =>
  ({ show, onUpdate, children, ...props }: Props) => {
    const [busy, setBusy] = useState(false);
    const [value, setValue] = useState(children);

    const initialExtRef = useRef(getFileExt(children));

    // track children state and update file extension ref
    useEffect(() => {
      initialExtRef.current = getFileExt(children);
    }, [children]);

    const ref = useRef<HTMLInputElement | null>(null);

    const handleUpdate = useMemo(
      () => async () => {
        if (!show) {
          return;
        }
        setBusy(true);

        try {
          let nextValue = value;
          // check if has extension and match initial extension
          if (
            initialExtRef.current &&
            !nextValue.endsWith(initialExtRef.current)
          ) {
            nextValue += "." + initialExtRef.current;
            setValue(nextValue);
          }
          await onUpdate(nextValue);
        } finally {
          setBusy(false);
        }
      },
      [onUpdate, show, value],
    );

    useOnClickOutside(ref, handleUpdate);

    const handleKeyDown = useCallback<
      ComponentPropsWithoutRef<typeof FormControl>["onKeyDown"]
    >(
      (e) => {
        if (show && !busy) {
          if (e.key === "Enter") {
            return handleUpdate();
          }

          if (e.key === "Escape") {
            setValue(children);
            return handleUpdate();
          }
        }
      },
      [busy, children, handleUpdate, show],
    );

    // should keep sync of div value every time when close the input
    useEffect(() => {
      if (!show) {
        setValue(children);
      }
    }, [children, show]);

    return show ? (
      <FormControl
        draggable={false}
        disabled={busy}
        type={"text"}
        className={props.className}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        value={value}
        ref={ref}
      />
    ) : (
      <Element {...props} children={children} />
    );
  };

export default withEditable;
